import React, { useEffect, useState } from "react";
import { Headset, Plus, Loader2, Trash2, CircleDot, Circle, Pencil, X, Check } from "lucide-react";
import PageHeader from "@/components/admin/PageHeader";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

const empty = { name: "", email: "", password: "", active: true };

export default function SettingsAgents() {
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(empty);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await adminApi.get("/api/admin/agents");
            setAgents(data.items || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const id = setInterval(load, 15000); // refresh online status
        return () => clearInterval(id);
    }, []);

    const openCreate = () => {
        setForm(empty);
        setEditingId(null);
        setShowForm(true);
    };

    const openEdit = (a) => {
        setForm({ name: a.name, email: a.email, password: "", active: a.active });
        setEditingId(a.id);
        setShowForm(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            if (editingId) {
                const payload = { name: form.name, email: form.email, active: form.active };
                if (form.password.trim()) payload.password = form.password.trim();
                await adminApi.put(`/api/admin/agents/${editingId}`, payload);
                toast.success("Agent updated");
            } else {
                await adminApi.post("/api/admin/agents", form);
                toast.success("Agent created");
            }
            setShowForm(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (a) => {
        if (!window.confirm(`Delete agent "${a.name}"? They will no longer be able to sign in.`)) return;
        try {
            await adminApi.delete(`/api/admin/agents/${a.id}`);
            toast.success("Agent deleted");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
        }
    };

    const inputCls =
        "w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15";

    return (
        <div className="p-10 max-w-3xl" data-testid="page-settings-agents">
            <PageHeader
                eyebrow="Settings"
                title="Live Agents"
                description="Create login accounts for your human hospitality experts. They sign in at /agent to handle guest chats handed off from Priya."
            />

            <div className="flex justify-between items-center mb-4">
                <div className="text-sm text-stone-500">
                    Agents sign in at{" "}
                    <a href="/agent/login" target="_blank" rel="noreferrer" className="text-orchid-700 font-medium underline">
                        /agent
                    </a>
                </div>
                <button
                    onClick={openCreate}
                    data-testid="add-agent-btn"
                    className="inline-flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-sm font-medium px-4 py-2 rounded-md"
                >
                    <Plus size={15} /> Add agent
                </button>
            </div>

            {showForm && (
                <div className="bg-white border border-orchid-700/30 rounded-xl p-5 mb-6 shadow-card" data-testid="agent-form">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-medium text-stone-900">{editingId ? "Edit agent" : "New agent"}</h3>
                        <button onClick={() => setShowForm(false)} className="text-stone-400 hover:text-stone-600">
                            <X size={16} />
                        </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="text-[11px] uppercase tracking-wider text-stone-500 block mb-1.5">Name</label>
                            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="agent-name" className={inputCls} />
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-wider text-stone-500 block mb-1.5">Email</label>
                            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="agent-email" className={`${inputCls} font-mono`} />
                        </div>
                    </div>
                    <div className="mt-4">
                        <label className="text-[11px] uppercase tracking-wider text-stone-500 block mb-1.5">
                            Password {editingId && <span className="text-stone-400 normal-case tracking-normal">(leave blank to keep current)</span>}
                        </label>
                        <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="agent-password" className={inputCls} placeholder="Min 6 characters" />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-stone-700 mt-4">
                        <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="agent-active" />
                        Active (can sign in)
                    </label>
                    <div className="flex justify-end gap-2 mt-5">
                        <button onClick={() => setShowForm(false)} className="text-sm px-4 py-2 rounded-md border border-stone-200 text-stone-600 hover:bg-stone-50">
                            Cancel
                        </button>
                        <button onClick={save} disabled={saving} data-testid="agent-save" className="inline-flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-sm font-medium px-4 py-2 rounded-md disabled:opacity-50">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                            {editingId ? "Save changes" : "Create agent"}
                        </button>
                    </div>
                </div>
            )}

            <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                {loading ? (
                    <div className="p-8 flex items-center gap-2 text-stone-400 text-sm">
                        <Loader2 size={15} className="animate-spin" /> Loading agents…
                    </div>
                ) : agents.length === 0 ? (
                    <div className="p-10 text-center text-stone-400">
                        <Headset size={32} strokeWidth={1.2} className="mx-auto mb-3" />
                        <p className="text-sm">No agents yet. Add your first hospitality expert.</p>
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[11px] uppercase tracking-wider text-stone-400 border-b border-stone-200">
                                <th className="px-4 py-3">Agent</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Presence</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {agents.map((a) => (
                                <tr key={a.id} className="border-b border-stone-100 last:border-0" data-testid={`agent-row-${a.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-stone-900">{a.name}</div>
                                        <div className="text-xs text-stone-500 font-mono">{a.email}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        {a.active ? (
                                            <span className="text-xs font-medium text-sage-700">Active</span>
                                        ) : (
                                            <span className="text-xs font-medium text-stone-400">Disabled</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        {a.online ? (
                                            <span className="inline-flex items-center gap-1.5 text-xs text-sage-700"><CircleDot size={12} /> Online</span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5 text-xs text-stone-400"><Circle size={12} /> Offline</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => openEdit(a)} data-testid={`edit-agent-${a.id}`} className="text-stone-400 hover:text-orchid-700 mr-3">
                                            <Pencil size={15} />
                                        </button>
                                        <button onClick={() => remove(a)} data-testid={`delete-agent-${a.id}`} className="text-stone-400 hover:text-red-600">
                                            <Trash2 size={15} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
